// GeexGridBars — Geex frame 08 (workspace/design-refs/geex/
// 08-analytics-grid-bars-orange.png, "Cash Receipts"): a faint same-hue
// grid rules the whole plot (via a repeating-linear-gradient background —
// no border/outline properties, so the borderless law holds); each
// column's value reads as a thin solid-ink CAP resting at the value
// height, not a filled column — the grid itself supplies the "how tall"
// reference. Optional `hatched` per item renders that column's body with
// the sanctioned hatch texture (the reference frame's "incomplete period"
// accent).
//
// items: [{ label, value, hatched? }].
// ink: base foreground (default 'title'). hue: optional — grid ruling +
// spacer tone shade the hue instead of ink-alpha (poster context).
// height: plot-band height — a spacing-scale token (default 'E2', ~161px
// at base scale).

export const GeexGridBars = {
  flow: 'y',
  gap: 'Y',
  items: [],
  ink: 'title',
  height: 'E2',

  Rail: {
    display: 'grid',
    gridTemplateColumns: (el) => `repeat(${(el.parent.items || []).length || 1}, 1fr)`,
    gap: 'X',
    childrenAs: 'state',
    children: (el, s) =>
      (el.parent.items || []).map((it, i) => ({ key: it.label || `p${i}`, label: it.label || '' })),
    childExtends: {
      tag: 'span',
      fontSize: 'Z',
      textAlign: 'center',
      opacity: '0.45',
      color: (el, s) => el.parent.parent.ink || 'title',
      // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
      // chart a bundle key (workspace/pages/admin/usage.js feeds
      // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
      // `translate()` returns its input unchanged when nothing resolves, so a
      // tenant-supplied label passes through untouched.
      text: (el, s) => el.call('polyglot', s.label || '')
    }
  },

  Plot: {
    position: 'relative',
    height: (el) => el.parent.height || 'E2',
    round: 'radiusCard',
    overflow: 'hidden',
    color: (el) => `${el.parent.hue || el.parent.ink || 'title'}.15`,
    backgroundImage:
      'repeating-linear-gradient(to right, currentColor 0, currentColor 1px, transparent 1px, transparent 24px), repeating-linear-gradient(to bottom, currentColor 0, currentColor 1px, transparent 1px, transparent 24px)',

    Cols: {
      position: 'absolute',
      inset: '0',
      display: 'grid',
      gridTemplateColumns: (el) => `repeat(${(el.parent.parent.items || []).length || 1}, 1fr)`,
      gap: 'X',
      padding: '0 X',
      childrenAs: 'state',
      children: (el, s) => {
        const parent = el.parent.parent
        const items = parent.items || []
        const max = items.reduce((n, it) => Math.max(n, Number(it.value) || 0), 0) || 1
        return items.map((it, i) => ({
          key: it.label || `col${i}`,
          pct: Math.max((Number(it.value) || 0) / max, 0.04) * 100,
          hatched: !!it.hatched,
          value: it.value,
          ink: parent.ink || 'title',
          hue: parent.hue
        }))
      },
      childExtends: {
        flow: 'y',
        justifyContent: 'flex-end',
        alignItems: 'center',
        height: '100%',
        gap: 'V',

        Value: {
          tag: 'span',
          fontSize: 'Z',
          fontFamily: 'Code',
          fontWeight: '700',
          color: (el, s) => s.ink,
          text: (el, s) => String(s.value ?? '')
        },

        Cap: {
          width: '100%',
          height: 'X',
          background: (el, s) => s.ink
        },

        Spacer: {
          width: '100%',
          height: (el, s) => `${s.pct}%`,
          color: (el, s) => `${s.hue || s.ink}.35`,
          background: 'transparent',
          backgroundImage: (el, s) =>
            s.hatched
              ? 'repeating-linear-gradient(135deg, currentColor 0, currentColor 3px, transparent 3px, transparent 7px)'
              : 'none'
        }
      }
    }
  }
}
